import styles from './cancelItemsTable.module.css'
import { useEffect, useMemo, useState } from "react"
import { Column, FieldType, FieldValue, NumberField, O, Section, Table } from "../../../../../features/ui"
import { Item, Operation } from "../../../../operations"
import { Lot, Product, ProductName, ProductType, useLotLoaders, useProductLoaders } from "../../../../products"
import { isDefined } from '../../../../../utils/typeUtils'
import { CancelItemRow } from '../../types'

type Props = {
    operation: Operation
    defaultRows: CancelItemRow[]
    title?: string
    onChange: (items: CancelItemRow[]) => void
}

export const CancelItemsTable = (props: Props) => {
    const { operation, defaultRows, title, onChange } = props

    const [products, setProducts] = useState<Product[]>([])
    const [lots, setLots] = useState<Lot[]>([])
    const [rows, setRows] = useState<CancelItemRow[]>(defaultRows)

    const productLoaders = useProductLoaders()
    const lotLoaders = useLotLoaders()

    const load = async (items: Item[]) => {
        const { loadProducts } = productLoaders()
        const loadedProducts = await loadProducts(items.map(item => item.productId))
        setProducts(loadedProducts)
        const { loadProductsLots } = lotLoaders()
        const loadedLots = await loadProductsLots(loadedProducts.map(product => product.id))
        setLots(loadedLots)
    }

    useEffect(() => {
        load(operation.items)
    }, [operation.id])

    useEffect(() => {
        setRows(defaultRows)
    }, [defaultRows])

    useEffect(() => {
        onChange(rows)
    }, [rows])

    const onChangeCancelQuantity = (itemId: string) => (value?: FieldValue) => {
        const newCancelQuantity = value as O<number>
        if (!isDefined(newCancelQuantity)) return
        setRows(rows.map(row => row.itemId === itemId ? {
            ...row,
            cancelQuantity: newCancelQuantity!
        } : row))
    }

    const onClickQuantity = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation()
    }

    const columns: Column[] = useMemo(() => [
        {
            name: 'itemId',
            type: FieldType.TEXT,
            label: 'Producto',
            render: (value?: FieldValue) => {
                const row = rows.find(row => row.itemId === value!)!
                const product = products.find(product => product.id === row.productId)
                const lot = lots.find(lot => lot.id === row.lotId)
                return <ProductName product={product} lot={lot} showSupplierCode />
            }
        },
        {
            name: 'pending',
            type: FieldType.NUMBER,
            label: 'Pendientes'
        },
        {
            name: 'cancelQuantity',
            type: FieldType.NUMBER,
            label: 'A Cancelar',
            render: (value?: FieldValue) => {
                const row = rows.find(row => row.itemId === value!)!
                const product = products.find(product => product.id === row.productId)
                return (
                    <NumberField
                        id="cancelQuantity"
                        value={row.cancelQuantity}
                        integer={product?.type !== ProductType.BULK}
                        min={0}
                        max={row.pending}
                        className={styles.qtyField}
                        onChange={onChangeCancelQuantity(row.itemId)}
                        onClick={onClickQuantity}
                    />
                )
            }
        }
    ], [rows, products, lots])

    const tableRows = useMemo(() => rows.map(row => ({
        itemId: row.itemId,
        pending: row.pending,
        cancelQuantity: row.itemId
    })), [rows])

    return (
        <Section
            id="cancel-items"
            title={title || "Productos"}
            space={12}
        >
            <Table
                columns={columns}
                rows={tableRows}
                rowId="itemId"
                readOnly
                alignRight={false}
            />
        </Section>
    )
}
